import MultipartForm from "@components/shared/MultipartForm";
import { useTextResources } from "@/language";
import CopySettings from "./pages/CopySettings";
import ConfirmDetails from "./pages/ConfirmDetails";
import Results from "./pages/Results";
import "../style.less";

/**
 * @typedef {Object} RateCustomName
 * @property {number} HotelId - The hotel ID
 * @property {string} HotelName - The hotel name
 * @property {string} RateName - The custom rate name
 * @property {boolean} IsNameValid - Whether the rate name is valid
 */

/**
 * @typedef {Object} CopyPricingFormState
 * @property {number} SourceCopyRateId - The ID of the rate being copied
 * @property {Object|null} SourceCopyRate - The source rate object
 * @property {Object} Components - Components to copy
 * @property {boolean} Components.General - Whether to copy general settings
 * @property {boolean} Components.Pricing - Whether to copy pricing information
 * @property {boolean} Components.Assignments - Whether to copy assignments
 * @property {boolean} Components.Content - Whether to copy content
 * @property {number[]} TargetHotelIds - Array of target hotel IDs
 * @property {RateCustomName[]} RateCustomNames - Array of custom names for the rates
 * @property {boolean} IsAllValid - Whether all rate names are valid
 */

const CopyPricingForm = ({ open, onClose, rate }) => {
  const text = useTextResources();

  const pages = [
    {
      title: text("CopySettings"),
      stepDescription: text("ChooseHotelsAndComponents"),
      content: (state, setState) => (
        <CopySettings state={state} setState={setState} />
      ),
      actions: [
        {
          content: text("Next"),
          onClick: (currentPage, setCurrentPage) => {
            setCurrentPage((prev) => prev + 1);
          },
          disabled: ({ TargetHotelIds, Components }) =>
            !TargetHotelIds ||
            TargetHotelIds.length === 0 ||
            Object.values(Components).every((v) => v === false),
          props: { primary: true },
        },
        {
          content: text("Close"),
          onClick: () => onClose(),
          disabled: false,
          props: {},
        },
      ],
    },
    {
      title: text("ConfirmDetails"),
      stepDescription: text("ReviewAndUpdateNames"),
      content: (state, setState) => (
        <ConfirmDetails state={state} setState={setState} />
      ),
      actions: [
        {
          content: "Copy",
          onClick: (currentPage, setCurrentPage) => {
            setCurrentPage((prev) => prev + 1);
          },
          disabled: ({ AllValid }) => !AllValid,
          props: { primary: true, icon: "copy" },
        },
        {
          content: "Close",
          onClick: (currentPage, setCurrentPage) => {
            onClose();
          },
          disabled: false,
          props: {},
        },
      ],
    },
    {
      title: text("Results"),
      stepDescription: text("CopySummary"),
      content: (state, setState) => (
        <Results state={state} setState={setState} />
      ),
      actions: [
        {
          content: text("Close"),
          onClick: () => onClose(),
          disabled: false,
          props: { primary: true },
        },
      ],
    },
  ];
  /** @type {CopyPricingFormState} */
  const initialState = {
    SourceCopyRateId: rate?.Id,
    SourceCopyRate: rate,
    Components: {
      General: false,
      Pricing: false,
      Assignments: false,
      Content: false,
    },
    TargetHotelIds: [],
    RateCustomNames: [],
    AllValid: false,
  };
  return (
    open && (
      <MultipartForm
        open={open}
        onClose={onClose}
        pages={pages}
        initialState={initialState}
        className="copy-pricing-form"
      />
    )
  );
};

export default CopyPricingForm;
